/**
 * Native camera integration. On iOS/Android (Capacitor) we call the OS
 * camera picker for higher quality and permission UX. On web we return null
 * so the caller falls back to the existing MediaDevices flow.
 */
import { isNative } from "./platform";

/** Returns a File object from the native camera, or null if not native/rejected. */
export async function captureNativePhoto(purpose = "device") {
  if (!isNative()) return null;
  try {
    // Dynamic import so web bundles don't pull in native code paths
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const image = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera,
      quality: 85,
      allowEditing: false,
      correctOrientation: true,
    });
    if (!image?.base64String) return null;
    const bytes = Uint8Array.from(atob(image.base64String), c => c.charCodeAt(0));
    const mime = image.format ? `image/${image.format}` : "image/jpeg";
    const filename = `${purpose}-${Date.now()}.${image.format || "jpg"}`;
    return new File([bytes], filename, { type: mime });
  } catch (err) {
    // User cancelled or permission denied — return null so UI shows nothing
    console.warn("Native camera capture aborted:", err?.message || err);
    return null;
  }
}

/** Picks from the device photo library (native only). Returns File or null. */
export async function pickNativePhoto(purpose = "device") {
  if (!isNative()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const image = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
      source: CameraSource.Photos,
      quality: 85,
      correctOrientation: true,
    });
    if (!image?.base64String) return null;
    const bytes = Uint8Array.from(atob(image.base64String), c => c.charCodeAt(0));
    const mime = image.format ? `image/${image.format}` : "image/jpeg";
    return new File([bytes], `${purpose}-${Date.now()}.${image.format || "jpg"}`, { type: mime });
  } catch (err) {
    console.warn("Native photo pick aborted:", err?.message || err);
    return null;
  }
}
